import { AppState, NamedTagGroup } from "./AppState";
import { AppStateJsonSerializer } from "./AppStateJsonSerializer";

export class TagManager {
	private isFirstOpening: boolean = true;

	constructor() {
		this.isFirstOpening = !this.isStoredStateAvailable();
	}

	readState(): AppState {
		const state = this.readAppState() ?? AppState.createEmpty();
		this.readSelectedTags(state);
		state.addListener((tag, selected) => this.storeSelectedTags(state));
		return state;
	}

	createStateWithSampleData(): AppState {
		const groups: NamedTagGroup[] = [
			{ name: "My Group 1", tags: ["#photography", "#photo"] },
			{ name: "My Group 2", tags: ["#people", "#model"] }
		];
		const state = new AppState(groups);
		this.isFirstOpening = false;
		return state;
	}

	saveAppState(state: AppState): void {
		let storage = this.getStorage();
		if (storage) {
			let serializedState = AppStateJsonSerializer.serialize(state);
			if (serializedState) {
				storage.setItem("state", serializedState);
			}
		}
	}

	resetState(): void {
		let storage = this.getStorage();
		if (storage) {
			storage.removeItem("state");
			storage.removeItem("selectedTags");
		}
		this.isFirstOpening = true;
	}

	readAppState(): AppState | null {
		let storage = this.getStorage();
		if (storage) {
			const serializedState = storage.getItem("state");
			if (serializedState) {
				return AppStateJsonSerializer.deserialize(serializedState);
			}
		}

		return null;
	}

	private isStoredStateAvailable(): boolean {
		let storage = this.getStorage();
		if (storage) {
			const serializedState = storage.getItem("state");
			return serializedState !== null;
		}

		return false;
	}

	get firstOpening(): boolean {
		return this.isFirstOpening;
	}

	private readSelectedTags(state: AppState) {
		let storage = this.getStorage();
		if (storage) {
			const serializedActiveTags = storage.getItem("selectedTags");
			if (serializedActiveTags) {
				try {
					const storedSelectedTags = JSON.parse(
						serializedActiveTags
					) as string[];
					if (storedSelectedTags) {
						storedSelectedTags.forEach(tag =>
							state.setTagSelection(tag, true)
						);
					}
				} catch (e) {
					console.error(
						"Configuration error: Could not read previously selected tags!"
					);
				}
			}
		}
	}

	private storeSelectedTags(state: AppState) {
		let storage = this.getStorage();
		if (storage) {
			try {
				const serializedSelectedTags = JSON.stringify(
					state.selectedTags
				);
				storage.setItem("selectedTags", serializedSelectedTags);
			} catch (e) {
				console.error(
					"Configuration error: Could not read previously selected tags!"
				);
			}
		}
	}

	private getStorage(): Storage | null {
		let storage: Storage | null = null;
		try {
			storage = window["localStorage"];
			let x = "__storage_test__";
			storage.setItem(x, x);
			storage.removeItem(x);
			return storage;
		} catch (e) {
			if (
				e instanceof DOMException &&
				// everything except Firefox
				(e.code === 22 ||
					// Firefox
					e.code === 1014 ||
					// test name field too, because code might not be present
					// everything except Firefox
					e.name === "QuotaExceededError" ||
					// Firefox
					e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
				// acknowledge QuotaExceededError only if there's something already stored
				storage &&
				storage.length !== 0
			) {
				return storage;
			}
		}

		return null;
	}
}
